import * as React from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { ApiError } from '@client/lib/api';
import { usePost } from '@client/lib/investor-api';
import { currentLocale } from '@client/lib/i18n';
import { track } from '@client/lib/tracker';
import { ErrorState, Spinner } from '@client/components/ui/feedback';
import { WatermarkLayer } from '@client/components/watermark';
import { PostArticle } from '@client/components/investor/post-article';
import { cn } from '@client/lib/utils';

export const Route = createFileRoute('/_investor/posts/$id')({ component: PostPage });

/** One Investor Update: by-line, metrics, rich body, linked room items; reading time is reported back when the page is left. */
function PostPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const locale = currentLocale();
  const post = usePost(id);
  const since = React.useRef(Date.now());

  React.useEffect(() => {
    since.current = Date.now();
    const send = () => {
      const ms = Date.now() - since.current;
      since.current = Date.now();
      if (ms < 1000) return;
      fetch(`/api/posts/${id}/read-time`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ duration_ms: ms }), credentials: 'same-origin', keepalive: true }).catch(() => undefined);
    };
    const onVis = () => { if (document.visibilityState === 'hidden') send(); else since.current = Date.now(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', send);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', send); send(); };
  }, [id]);

  if (post.isPending) return <Spinner label={t('common.loading')} />;
  if (post.isError || !post.data) {
    const err = post.error as ApiError;
    return <main className="mx-auto max-w-[640px] px-5 py-16"><ErrorState title={t('common.notFound')} body={err instanceof ApiError && err.reason ? t(`errors.${err.reason}`, { defaultValue: t('errors.denied') }) : t('errors.denied')} onRetry={() => post.refetch()} retryLabel={t('common.retry')} /></main>;
  }
  const d = post.data;
  return (
    <main className={cn('relative mx-auto max-w-[860px] px-5 py-8 md:px-8', !d.controls.copy && 'select-none', !d.controls.print && 'no-print')} onCopy={(e) => { if (!d.controls.copy) e.preventDefault(); }}>
      {d.watermark ? <WatermarkLayer text={d.watermark} /> : null}
      <div className="flex flex-wrap items-center gap-3 text-[13px]">
        <Link to="/updates" search={{}} className="inline-flex items-center gap-1.5 no-underline text-muted hover:text-foreground"><ArrowLeft className="size-4" />{t('updates.title')}</Link>
        {d.room ? <><span className="text-muted">·</span><Link to="/rooms/$slug" params={{ slug: d.room.slug }} className="no-underline text-muted hover:text-foreground">{d.room.name}</Link></> : null}
      </div>
      <PostArticle className="mt-5" post={d.post} locale={locale} attachments={d.attachments} onAttachmentClick={(a) => track('post.attachment_clicked', { room_id: a.room?.id, item_id: a.id, meta: { post_id: d.post.id } })} />
    </main>
  );
}
